import farmIllustration from 'assets/img/agent-farm-illustration.svg';
import icons from 'assets/img/agent-onboarding-icons.svg';
import React from 'react';
import { Link } from 'react-router-dom';
import type { AgentDashboardDraft } from 'types/agent-dashboard';

import './AgentOnboarding.css';

import { Dialog } from '@material-ui/core';

const Icon = ({ name, className = '' }: { name: string; className?: string }) => (
  <svg className={`icon ${className}`} aria-hidden="true"><use href={`${icons}#i-${name}`} /></svg>
);
const draftUrl = (draft: AgentDashboardDraft) => `/dashboard/create-campaign?campaign=${encodeURIComponent(draft.id)}`;
const areaLabel = (draft: AgentDashboardDraft) => (/^\d{5}$/.test(draft.farmAreaName) ? `ZIP ${draft.farmAreaName}` : draft.farmAreaName);
const draftArea = (draft: AgentDashboardDraft) => [areaLabel(draft), draft.campaignType].filter(Boolean).join(' · ');

const AgentOnboarding = ({ firstName, drafts }: { firstName?: string; drafts: AgentDashboardDraft[] }) => {
  const [dialog, setDialog] = React.useState<'formula' | 'help' | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const openDialog = (content: 'formula' | 'help') => {
    setDialog(content);
    setDialogOpen(true);
  };
  // Keep the content and paper dimensions intact until MUI finishes its exit transition.
  const closeDialog = () => setDialogOpen(false);
  const draft = drafts[0];
  const hasAudience = draft?.nextStep === 2;
  const name = firstName ? `, ${firstName}` : '';
  const titles = {
    formula: 'Three signals. One focused audience.',
    help: 'Schedule a call with our team',
  };
  const formula = (
    <section className="formula" aria-labelledby="onboarding-formula-title">
      <div className="formula-copy">
        {!draft && <div className="eyebrow">Smarter targeting</div>}
        <h2 id="onboarding-formula-title">Start with the right signals.</h2>
        <p>Harvist’s high propensity to sell formula helps you focus your farm using three property signals.</p>
        <button type="button" className="text-button" onClick={() => openDialog('formula')}>
          How the formula works
          {' '}
          <Icon name="arrow" />
        </button>
      </div>
      <dl className="signals">
        <div className="signal">
          <dt>
            Since the last
            <br />
            property transfer
          </dt>
          <dd>
            10+
            {' '}
            <span>years</span>
          </dd>
        </div>
        <div className="signal">
          <dt>
            Loan-to-value
            <br />
            ratio
          </dt>
          <dd>
            50%
            <span className="qualifier">or less</span>
          </dd>
        </div>
        <div className="signal">
          <dt>
            Mortgage
            <br />
            interest rate
          </dt>
          <dd>
            5%+
            <span className="qualifier">estimated</span>
          </dd>
        </div>
      </dl>
    </section>
  );

  return (
    <div className={`agent-onboarding${draft ? ' has-draft' : ''}`}>
      <header className="welcome">
        <h1>{draft ? `Welcome back${name}.` : `Welcome to Harvist${name}`}</h1>
        <p>{draft ? 'Pick up where you left off.' : 'Let’s get your name out there.'}</p>
      </header>
      <section className="launch-card" aria-labelledby="onboarding-launch-title">
        <div className="hero">
          <div className="hero-copy">
            <div className="eyebrow">
              <Icon name="spark" />
              Your first campaign
            </div>
            <h2 id="onboarding-launch-title">
              {draft ? (
                <>
                  Your first campaign
                  <br />
                  is taking shape.
                </>
              ) : (
                <>
                  Become the agent
                  <br />
                  they remember.
                </>
              )}
            </h2>
            <p>
              {draft
                ? hasAudience
                  ? 'Your farm area is selected. Next, personalize the touches that put your name and face in front of homeowners.'
                  : 'Your campaign draft is saved. Next, finish choosing the homeowners you want to reach in your farm area.'
                : 'Put your name and face in front of homeowners in your farm area. Start a campaign and let Harvist help you keep showing up.'}
            </p>
            {draft && (
              <div className="draft-summary">
                <div className="draft-summary-top">
                  <span className="draft-badge">Saved draft</span>
                  <Link className="text-button" to="/dashboard/campaigns">
                    View all drafts (
                    {drafts.length}
                    )
                  </Link>
                </div>
                <h3>{draft.name || 'Untitled campaign'}</h3>
                {draftArea(draft) && (
                  <p>
                    {areaLabel(draft)}
                    {draft.farmAreaName && draft.campaignType && (
                      <>
                        {' '}
                        <span aria-hidden="true">·</span>
                        {' '}
                      </>
                    )}
                    {draft.campaignType}
                  </p>
                )}
              </div>
            )}
            <div className="hero-cta">
              <Link className="primary" to={draft ? draftUrl(draft) : '/dashboard/create-campaign'}>
                {draft ? 'Continue your campaign' : 'Create your first campaign'}
                {' '}
                <Icon name="arrow" />
              </Link>
            </div>
          </div>
          <div className="farm-visual" role="img" aria-label="Illustration of a farm area, with highlighted homes and branded outreach. No campaign has launched.">
            <img className="farm-map" src={farmIllustration} alt="" />
            <div className="map-tag" aria-hidden="true">
              <Icon name="pin" />
              {hasAudience ? 'Your selected farm area' : 'Your future farm area'}
            </div>
            <div className="brand-preview" aria-hidden="true">
              <div className="portrait"><Icon name="users" /></div>
              <div>
                <strong>Your name. Your face. Your brand.</strong>
                <p>A familiar presence, one touch at a time.</p>
              </div>
              <Icon name="mail" />
            </div>
          </div>
        </div>
        <ol className="steps" aria-label={draft ? 'Your saved campaign progress' : 'Your path to launch'}>
          <li className={hasAudience ? 'step-complete' : draft ? 'step-current' : ''} aria-current={draft && !hasAudience ? 'step' : undefined}>
            <span className="step-number">{hasAudience ? <Icon name="check" /> : '1'}</span>
            <div>
              <h3>
                Choose your farm area
                {draft && !hasAudience && <span className="current-label">Up next</span>}
              </h3>
              <p>{hasAudience ? 'Completed · Your audience is selected.' : 'Find the homeowners you want to reach.'}</p>
            </div>
          </li>
          <li className={hasAudience ? 'step-current' : ''} aria-current={hasAudience ? 'step' : undefined}>
            <span className="step-number">2</span>
            <div>
              <h3>
                Make it yours
                {' '}
                {hasAudience && <span className="current-label">Up next</span>}
              </h3>
              <p>Personalize your branded touches.</p>
            </div>
          </li>
          <li>
            <span className="step-number">3</span>
            <div>
              <h3>Review &amp; launch</h3>
              <p>{draft ? 'Confirm everything before it starts.' : 'Approve your campaign before it starts.'}</p>
            </div>
          </li>
        </ol>
      </section>
      {draft ? (
        <details className="targeting-refresher">
          <summary>
            <span className="refresher-icon"><Icon name="spark" /></span>
            <span>
              <strong>A quick refresher on smarter targeting</strong>
              <span>Explore Harvist’s high propensity to sell formula.</span>
            </span>
            <Icon name="chevron" className="chevron" />
          </summary>
          {formula}
        </details>
      ) : formula}
      <section className="next-note" aria-labelledby="onboarding-next-title">
        <Icon name="chart" />
        <div>
          <h2 id="onboarding-next-title">Once you launch, this is your home base.</h2>
          <p>See your brand’s activity, spot prospect engagement, and know who to follow up with.</p>
        </div>
        <button type="button" className="text-button" onClick={() => openDialog('help')}>
          Need a hand getting started?
          <Icon name="arrow" />
        </button>
      </section>
      <footer className="footer"><span>Built for the relationships that grow your business.</span></footer>
      <Dialog open={dialogOpen} onClose={closeDialog} aria-labelledby="onboarding-dialog-title" maxWidth={false} PaperProps={{ className: `agent-onboarding-dialog${dialog === 'help' ? ' agent-onboarding-booking-dialog' : ''}${dialog === 'formula' ? ' agent-onboarding-formula-dialog' : ''}` }} BackdropProps={{ className: 'agent-onboarding-backdrop' }}>
        <div className="dialog-head">
          <div>
            {dialog === 'formula' && <div className="dialog-kicker">High propensity to sell</div>}
            <h2 id="onboarding-dialog-title">{dialog && titles[dialog]}</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Close dialog" onClick={closeDialog}><Icon name="close" /></button>
        </div>
        <div className="dialog-content">
          {dialog === 'formula' && (
            <>
              <p>For eligible residential properties, Harvist looks for all three of these criteria together:</p>
              <ul>
                <li>
                  <strong>10+ years since the last transfer.</strong>
                  {' '}
                  The recorded property transfer date is at least ten years ago.
                </li>
                <li>
                  <strong>50% loan-to-value or less.</strong>
                  {' '}
                  The loan balance is no more than half the estimated property value.
                </li>
                <li>
                  <strong>Estimated mortgage rate of 5% or higher.</strong>
                  {' '}
                  This uses the estimated rate on the first-position loan.
                </li>
              </ul>
              <p>Choose high-propensity targeting when setting up a Full Farm or Absentee Owners campaign. You can review the available audience before you launch.</p>
              <p>These are targeting signals, not a guarantee that an owner will sell.</p>
            </>
          )}
          {dialog === 'help' && (
            <iframe
              className="onboarding-booking-frame"
              title="Book a Harvist onboarding call"
              src="https://api.leadconnectorhq.com/widget/bookings/todd-liebman-personal-calendar-zm9tcvfwa"
            />
          )}
        </div>
        {dialog !== 'help' && <div className="dialog-footer"><button type="button" className="primary" onClick={closeDialog}>Got it</button></div>}
      </Dialog>
    </div>
  );
};

export default AgentOnboarding;
